import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './PanelControlElectivos.css';

export default function PanelControlElectivos() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [stats, setStats] = useState([]);   // [{ codigo, nombre, bloque, students: [...] }, …]
  const [catalog, setCatalog] = useState([]); // [{ codigo, nombre }, …]
  const [allChoices, setAllChoices] = useState([]); // [{ rut, nombre, curso, bloque, primary, secondary }, …]
  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState('');

  const API = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');

  // Construye un diccionario código → nombre
  const codeNameMap = useMemo(() => {
    const m = {};
    catalog.forEach(c => {
      m[c.codigo] = c.nombre;
    });
    return m;
  }, [catalog]);

  // Al montar: cargar flag, stats, catálogo y elecciones
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/student/admin/settings/toma_ramos`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/student/admin/electivos/stats`,   { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/student/admin/electivos/catalogo`,{ headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/student/admin/electivos/choices`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
    .then(([resSet, resStats, resCat, resChoices]) => {
      setEnabled(Boolean(resSet.data.enabled));
      setStats(Array.isArray(resStats.data) ? resStats.data : []);
      setCatalog(Array.isArray(resCat.data)  ? resCat.data  : []);
      setAllChoices(Array.isArray(resChoices.data) ? resChoices.data : []);
      if (resCat.data?.length) {
        setSelected(resCat.data[0].codigo);
      }
    })
    .catch(err => {
      console.error(err);
      setMessage('Error cargando datos');
    })
    .finally(() => setLoading(false));
  }, [API, token]);

  // Toggle del flag de toma de ramos
  const toggleEnabled = async () => {
    setMessage('');
    try {
      const res = await axios.put(
        `${API}/student/admin/settings/toma_ramos`,
        { enabled: !enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnabled(Boolean(res.data.enabled));
      setMessage(`Toma de ramos ${res.data.enabled ? 'habilitada' : 'deshabilitada'} ✔️`);
    } catch (err) {
      console.error(err);
      setMessage('Error al cambiar el estado');
    }
  };

  if (loading) return <p>Cargando panel de control…</p>;

  return (
    <div className="panel-control">
      <h1>Panel de Control Electivos</h1>
      {message && <div className="message">{message}</div>}

      <section className="toggle-section">
        <button
          className={enabled ? 'btn-toggle off' : 'btn-toggle on'}
          onClick={toggleEnabled}
        >
          {enabled ? 'Deshabilitar toma de ramos' : 'Habilitar toma de ramos'}
        </button>
      </section>

      <section className="stats-section">
        <h2>Estadísticas por Electivo</h2>
        <label htmlFor="selElectivo">Filtrar electivo:</label>
        <select
          id="selElectivo"
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          {catalog.map(c => (
            <option key={c.codigo} value={c.codigo}>
              {c.codigo} – {c.nombre}
            </option>
          ))}
        </select>

        <table className="stats-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Primera Opción</th>
              <th>Segunda Opción</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const entry = stats.find(e => e.codigo === selected) ?? { codigo: selected, nombre: catalog.find(c => c.codigo === selected)?.nombre || '', bloque: null, students: [] };
              const primaryCount = entry.students.filter(s => s.opcion === 1).length;
              const secondaryCount = entry.students.filter(s => s.opcion === 2).length;
              return (
                <tr>
                  <td>{entry.codigo}</td>
                  <td>{entry.nombre}</td>
                  <td>{primaryCount}</td>
                  <td>{secondaryCount}</td>
                </tr>
              );
            })()}
          </tbody>
        </table>
      </section>

      <section className="details-section">
        <h2>Detalle de Postulantes Primarios</h2>
        <table className="details-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Curso</th>
              <th>Nota Final</th>
              <th>Segunda Opción</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const entry = stats.find(e => e.codigo === selected) ?? { codigo: selected, nombre: catalog.find(c => c.codigo === selected)?.nombre || '', bloque: null, students: [] };
              return entry.students
                .filter(s => s.opcion === 1)
                .map(s => {
                  const choice = allChoices.find(x => x.rut === s.rut && x.bloque === entry.bloque) || {};
                  const secName = choice.secondary ? codeNameMap[choice.secondary] : '-';
                  return (
                    <tr key={s.rut}>
                      <td>{s.nombre}</td>
                      <td>{s.curso}</td>
                      <td>{s.nota_final != null ? s.nota_final : '-'}</td>
                      <td>{secName}</td>
                    </tr>
                  );
                });
            })()}
          </tbody>
        </table>
      </section>
      
      <div className="export-buttons">
          <button className="btn-export" onClick={() => {
            const wb = XLSX.utils.book_new();
            catalog.forEach(c => {
              const entry = stats.find(e => e.codigo === c.codigo) ?? { codigo: c.codigo, nombre: c.nombre, bloque: null, students: [] };
              const rows = entry.students
                .filter(s => s.opcion === 1)
                .map(s => {
                  const choice = allChoices.find(x => x.rut === s.rut && x.bloque === entry.bloque) || {};
                  const secName = choice.secondary ? codeNameMap[choice.secondary] : '';
                  return {
                    Nombre:           s.nombre,
                    Curso:            s.curso,
                    'Nota Final':     s.nota_final != null ? s.nota_final : '',
                    'Segunda Opción': secName
                  };
                });
              const ws = XLSX.utils.json_to_sheet(rows, { header: ['Nombre', 'Curso', 'Nota Final', 'Segunda Opción'] });
              const sheetName = c.nombre.length <= 31 ? c.nombre : c.nombre.slice(0, 28) + '…';
              XLSX.utils.book_append_sheet(wb, ws, sheetName);
            });
            XLSX.writeFile(wb, 'Estudiantes_por_electivo.xlsx');
          }}>
            Exportar Listas
          </button>
        </div>
    </div>
  );
}
